import { BenchmarkingDashboardDetails } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingDashboardDetails";
import { BenchmarkingProviderFlyoutData } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingProviderFlyoutData";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/NetworkComparisonFiltersPayload";
import { BenchmarkingFilterData } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingFilterData";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingLineOfBusinessEnum";
import { MedicareNetwork } from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { MedicareNetworkFilterData } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/shared/MedicareNetworkFilterData";
import { MedicarePrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/shared/MedicarePrimaryFiltersPayload";
import { CompetitiveInsightsSupersetProjectConfigurations } from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsSupersetProjectConfigurations";
import { EffectivenessAndEfficiencyData, HospitalAndPcpCountData, MarketPenetrationData, ProvidersByNetworkAndVolumeData, ProvidersByNetworkData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { AffiliationsData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared/AffiliationsData";
import { ProviderOverlapData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared/ProviderOverlapData";
import { authenticatedDownloadAsync } from "@app/shared/AuthenticatedDownload";
import ServiceBase from "@app/shared/serviceBase";

/** handles communication across the wire when interacting with the network benchmarking dashboard */
export default class BenchmarkingService extends ServiceBase {
  private benchmarkingEndpoint = `${this.apiEndpoint}/benchmarking`;
  public readonly lineOfBusiness = BenchmarkingLineOfBusinessEnum.Medicare;

  public async getBenchmarkingLinesOfBusiness(signal?: AbortSignal): Promise<BenchmarkingLineOfBusinessEnum[]> {
      return await this.makeGet<BenchmarkingLineOfBusinessEnum[]>(`lines-of-business`, null, this.benchmarkingEndpoint, false, undefined, signal);
  }

  public async getBenchmarkingPrimaryFilterData(signal?: AbortSignal): Promise<BenchmarkingFilterData> {
    return await this.makeGet<BenchmarkingFilterData>(`primary-filters`, null, this.benchmarkingEndpoint, false, undefined, signal);
  }

  public async getBenchmarkingNetworkData(primaryFilters: MedicarePrimaryFiltersPayload): Promise<MedicareNetworkFilterData> {
    if (!primaryFilters) {
      return null;
    }
    return await this.makePost<MedicareNetworkFilterData>(`get-networks`, primaryFilters, this.benchmarkingEndpoint);
  }

  public async getContractDetails(contractId: string, primaryFilters: MedicarePrimaryFiltersPayload, versionId?: number, signal?: AbortSignal): Promise<MedicareNetwork> {
    if (!primaryFilters || !contractId) {
      return null;
    }

    return await this.makePost<MedicareNetwork>(`contract/${contractId}`, {...primaryFilters, versionId}, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getHospitalAndPcpCountData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<HospitalAndPcpCountData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<HospitalAndPcpCountData[]>(`hospital-and-pcp-count`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProvidersByNetworkData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<ProvidersByNetworkData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<ProvidersByNetworkData[]>(`providers-by-network`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getMarketPenetrationData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<MarketPenetrationData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<MarketPenetrationData[]>(`market-penetration`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProvidersByNetworkAndVolumeData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<ProvidersByNetworkAndVolumeData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<ProvidersByNetworkAndVolumeData[]>(`providers-by-network-and-volume`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getEffectivenessAndEfficiencyData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<EffectivenessAndEfficiencyData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<EffectivenessAndEfficiencyData[]>(`effectiveness-and-efficiency`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProviderOverlapData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<ProviderOverlapData> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<ProviderOverlapData>(`provider-overlap`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getAffiliationsData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<AffiliationsData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<AffiliationsData[]>(`affiliations`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getDashboardDetailsData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<BenchmarkingDashboardDetails[]> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<BenchmarkingDashboardDetails[]>(`dashboard-details`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getProviderFlyoutData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload, signal?: AbortSignal): Promise<BenchmarkingProviderFlyoutData> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<BenchmarkingProviderFlyoutData>(`provider-details`, networkComparisonFiltersPayload, this.benchmarkingEndpoint, false, "application/json", true, undefined, signal);
  }

  public async getSupersetProjectConfigurations(signal?: AbortSignal): Promise<CompetitiveInsightsSupersetProjectConfigurations[]> {
    return await this.makeGet<CompetitiveInsightsSupersetProjectConfigurations[]>(`superset-project-configurations`, null, this.benchmarkingEndpoint, false, undefined, signal);
  }

  public async getDataCoverageCsv(fileName?: string, clientId?: number): Promise<void> {
      let urlSearchParams = new URLSearchParams();

      if (!!clientId) {
          const query = {
              clientId: clientId.toString()
          };
          urlSearchParams = new URLSearchParams(query)
      }

      if (!!fileName && fileName.split('.').pop().toLowerCase() !== `csv`) {
          fileName += `.csv`;
      }

      return await authenticatedDownloadAsync(`${this.benchmarkingEndpoint}/data-coverage/report/csv`, null, fileName, urlSearchParams);
  }
}
